import fileDownload from "js-file-download";
import posthog from "posthog-js";
import { getCtoStatusCountByVessel } from "../../../mappers/cto/ctoMapper";
import { ControlStatus, CtoStatus, HeaderStatus } from "../../../types";
import { Response } from "../../../types/serviceTypes";
import {
  BodyManualCloseCto,
  ControlStatusCounts,
  Cto,
  CtoDashboard,
  CtoExpiryDateHistory,
  CtoStatusCounts,
  CtoSummary,
  ExtendCtoBody,
  ExtendDueDateBody,
  RecommendationDueDateHistoryData,
  Recommendations,
  ResponsePostCto,
  ResponsePostExtendDueDate,
} from "../../../types/cto";
import { PDF_TYPE } from "../../../utils/fileFormats";
import { downloadAndSaveFile } from "../../../utils/downloadAndSaveFile";
import { workflowInstance_V2 } from "../../api";
import {
  buildResponse,
  buildResponseWithMapper,
  callWorkflowGET,
  callWorkflowGET_V3,
  callWorkflowPOST,
  callWorkflowPOST_V3,
} from "../../generic/genericService";
import { CTO_ENDPOINTS } from "../endpoints";

interface CtoByVesselProps {
  vesselCode: string;
  ctoCode: string;
  ctoVersion?: number;
  setObject: (obj: Response<Cto>) => void;
}

export const postExtendCto = (
  vesselCode: string,
  ctoCode: string,
  body: ExtendCtoBody,
  setObject: (obj: Response<ResponsePostCto>) => void
): void => {
  const response = callWorkflowPOST_V3<ResponsePostCto, ExtendCtoBody>(
    CTO_ENDPOINTS.VESSEL_CTO_EXTEND(vesselCode, ctoCode),
    body
  );

  buildResponse<ResponsePostCto>(response, setObject);
};

export const postExtendCtoAsync = (
  vesselCode: string,
  ctoCode: string,
  body: ExtendCtoBody,
  setObject: (obj: Response<ResponsePostCto>) => void
): Promise<ResponsePostCto> => {
  return new Promise(() => {
    postExtendCto(vesselCode, ctoCode, body, setObject);
  });
};

export const postExtendDueDate = (
  vesselCode: string,
  ctoCode: string,
  recommendation_id: number,
  body: ExtendDueDateBody,
  setObject: (obj: Response<ResponsePostExtendDueDate>) => void
): void => {
  const response = callWorkflowPOST<
    ResponsePostExtendDueDate,
    ExtendDueDateBody
  >(
    CTO_ENDPOINTS.VESSEL_RECOMMENDATION_EXTEND(
      vesselCode,
      ctoCode,
      recommendation_id
    ),
    body
  );

  buildResponse<ResponsePostExtendDueDate>(response, setObject);
};

export const postExtendDueDateAsync = (
  vesselCode: string,
  ctoCode: string,
  recommendation_id: number,
  body: ExtendDueDateBody,
  setObject: (obj: Response<ResponsePostExtendDueDate>) => void
): Promise<void> => {
  return new Promise((resolve) => {
    postExtendDueDate(vesselCode, ctoCode, recommendation_id, body, setObject);
    resolve();
  });
};

export const getCtosByVessel = (
  vesselCode: string,
  setObject: (obj: Response<Cto[]>) => void
): void => {
  const response = callWorkflowGET_V3<Cto[]>(
    CTO_ENDPOINTS.VESSEL_CTO_LEGACY(vesselCode)
  );
  buildResponse<Cto[]>(response, setObject);
};
///api/v2/eps/cto/summary
export const getCtosByVesselEps = (
  vesselCode: string,
  setObject: (obj: Response<Cto[]>) => void
): void => {
  const response = callWorkflowGET<Cto[]>(
    CTO_ENDPOINTS.VESSEL_CTO_LEGACY(vesselCode)
  );
  buildResponse<Cto[]>(response, setObject);
};

export const fetchCtosByVesselDashboard = (vesselCode: string) =>
  callWorkflowGET<CtoDashboard[]>(CTO_ENDPOINTS.VESSEL_CTOS(vesselCode));

export const fetchRecommendationByVesselSlice = (
  vesselCode: string,
  selectedCtoStatusIndex?: number,
  selectedRecommendationStatus?: ControlStatus
): Promise<Recommendations> =>
  callWorkflowGET<Recommendations>(
    CTO_ENDPOINTS.VESSEL_RECOMMENDATIONS(vesselCode),
    {
      params: {
        cto_status: selectedCtoStatusIndex,
        recommendation_status: selectedRecommendationStatus,
      },
    }
  );

export const fetchRecommendationStatusByVessel = (
  vesselCode: string,
  selectedCtoStatusIndex?: number
): Promise<ControlStatusCounts> =>
  callWorkflowGET<ControlStatusCounts>(
    CTO_ENDPOINTS.VESSEL_RECOMMENDATION_SUMMARY(vesselCode),
    {
      params: { cto_status: selectedCtoStatusIndex },
    }
  );

export const fetchCtoStatusByVessel = (
  vesselCode: string,
  selectedRecommendationStatus?: ControlStatus
): Promise<CtoStatusCounts> =>
  callWorkflowGET_V3<CtoStatusCounts>(
    CTO_ENDPOINTS.VESSEL_CTO_STATUS(vesselCode),
    {
      params: { recommendation_status: selectedRecommendationStatus },
    }
  );

export const getCtoByVessel = ({
  vesselCode,
  ctoCode,
  ctoVersion,
  setObject,
}: CtoByVesselProps): void => {
  const response = callWorkflowGET_V3<Cto>(
    CTO_ENDPOINTS.VESSEL_CTO_DETAIL(vesselCode, ctoCode),
    {
      params: { version: ctoVersion },
    }
  );
  buildResponse<Cto>(response, setObject);
};

export const postManualCloseCto = (
  vesselCode: string,
  ctoCode: string,
  body: BodyManualCloseCto,
  setObject: (obj: Response<ResponsePostCto>) => void
): void => {
  const response = callWorkflowPOST<ResponsePostCto, BodyManualCloseCto>(
    CTO_ENDPOINTS.VESSEL_CTO_CLOSE(vesselCode, ctoCode),
    body
  );

  buildResponse<ResponsePostCto>(response, setObject);
};

export const getCtoSummary = (
  country: string,
  setObject: (obj: Response<CtoSummary[]>) => void
): void => {
  const response = callWorkflowGET<CtoSummary[]>(
    `${CTO_ENDPOINTS.CTO_SUMMARY}?country_code=${country}`
  );
  buildResponse<CtoSummary[]>(response, setObject);
};

export const getCtoSummaryByVessel = (
  country: string,
  vessel: string,
  setObject: (obj: Response<HeaderStatus[]>) => void
): void => {
  const response = callWorkflowGET<CtoSummary[]>(
    `${CTO_ENDPOINTS.CTO_SUMMARY}?country_code=${country}`
  );

  buildResponseWithMapper<CtoSummary[], HeaderStatus[]>(
    response,
    setObject,
    (response) => getCtoStatusCountByVessel(response, vessel)
  );
};

export const getCtoStepGeneric = <T>(
  vesselCode: string,
  ctoCode: string,
  stepSubdirectory: string,
  setObject: (obj: Response<T>) => void,
  version?: number
): void => {
  const response = callWorkflowGET<T>(
    `${CTO_ENDPOINTS.VESSEL_CTO_DETAIL(
      vesselCode,
      ctoCode
    )}/${stepSubdirectory}`,
    {
      params: { version },
    }
  );
  buildResponse<T>(response, setObject);
};

export const getRecommendationDueDateHistory = (
  vesselCode: string,
  ctoCode: string,
  recommendationId: number,
  setObject: (obj: Response<RecommendationDueDateHistoryData>) => void
): void => {
  const response = callWorkflowGET_V3<RecommendationDueDateHistoryData>(
    CTO_ENDPOINTS.VESSEL_RECOMMENDATION_DUE_DATE_HISTORY(
      vesselCode,
      ctoCode,
      recommendationId
    )
  );
  buildResponse<RecommendationDueDateHistoryData>(response, setObject);
};

export const getCtosRecommendationsExport = (
  vesselCode: string,
  format: string,
  selectedCtoStatus?: CtoStatus,
  selectedRecommendationStatus?: ControlStatus
): void => {
  const endpoint = CTO_ENDPOINTS.VESSEL_RECOMMENDATIONS(vesselCode);
  const fileName = `${vesselCode}_CTOs_Recommendations`;
  const params: Record<string, string> = {};

  if (selectedCtoStatus !== undefined) {
    params.cto_status = String(selectedCtoStatus);
  }

  if (selectedRecommendationStatus !== undefined) {
    params.recommendation_status = selectedRecommendationStatus;
  }

  downloadAndSaveFile(
    endpoint,
    fileName,
    format,
    Object.keys(params).length > 0 ? params : undefined
  );
};

export const getCtosStatusExport = (
  vesselCode: string,
  format: string
): void => {
  const endpoint = CTO_ENDPOINTS.VESSEL_CTO_LEGACY(vesselCode);
  const fileName = `${vesselCode}_CTOs`;

  downloadAndSaveFile(endpoint, fileName, format);
};

export const getCtoListExport = (vesselCode: string, format: string): void => {
  const endpoint = CTO_ENDPOINTS.VESSEL_CTOS(vesselCode);
  const fileName = `${vesselCode}_CTOs`;

  downloadAndSaveFile(endpoint, fileName, format);
};

export const getCtosStatusExportPDF = async (
  vesselCode: string,
  ctoCode: string
): Promise<void> => {
  const response = await workflowInstance_V2.get(
    CTO_ENDPOINTS.VESSEL_CTO_EXPORT(vesselCode, ctoCode),
    {
      responseType: "blob",
    }
  );
  const aBlob = new Blob([response.data], { type: PDF_TYPE });
  fileDownload(aBlob, ctoCode, PDF_TYPE);
};

export const fetchStatusDataByVessel = (
  vesselId: string,
  setCto: (res: Response<CtoStatusCounts>) => void,
  setRecommendation: (res: Response<ControlStatusCounts>) => void,
  selectedCtoStatusIndex?: number,
  selectedRecommendationStatus?: ControlStatus
): void => {
  setCto({ data: undefined, loading: true, error: false });
  setRecommendation({ data: undefined, loading: true, error: false });

  Promise.all([
    fetchCtoStatusByVessel(vesselId, selectedRecommendationStatus),
    fetchRecommendationStatusByVessel(vesselId, selectedCtoStatusIndex),
  ])
    .then(([cto, recommendation]) => {
      setCto({ data: cto, loading: false, error: false });
      setRecommendation({ data: recommendation, loading: false, error: false });
    })
    .catch((error) => {
      setCto({ data: undefined, loading: false, error: true });
      setRecommendation({ data: undefined, loading: false, error: true });
      posthog.captureException(error);
    });
};

export const getCtoExpiryDateHistory = (
  vesselCode: string,
  ctoCode: string,
  setObject: (obj: Response<CtoExpiryDateHistory>) => void
): void => {
  const response = callWorkflowGET<CtoExpiryDateHistory>(
    CTO_ENDPOINTS.VESSEL_CTO_EXPIRY_DATE_HISTORY(vesselCode, ctoCode)
  );
  buildResponse<CtoExpiryDateHistory>(response, setObject);
};
